import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignInButton } from '@clerk/clerk-react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Zap as ZapIcon,
  Target as TargetIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Linkedin as LinkedInIcon,
  MapPin as MapPinIcon,
  TrendingUp as TrendingUpIcon,
  Infinity as InfinityIcon,
  Crown as CrownIcon,
  Settings as SettingsIcon,
  Users as UsersIcon,
  Sparkles as SparklesIcon,
  Building2 as BuildingIcon,
} from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { createPortalSession } from '../services/api';
import { trackFunnelEvent } from '../services/funnel';
import { fbTrack } from '../services/pixel';
import { useUser } from '@clerk/clerk-react';
import { designSystem } from '../theme';

// ── Role-specific content ──

const ROLE_CONTENT = {
  seeker: {
    headline: 'Land Your Next Job Faster',
    subtitle: 'Message LinkedIn recruiters, unlock outreach tools, and move faster in your job search',
    ctaText: 'Start finding jobs faster',
    proStatusText: 'Enjoying all Pro benefits including unlimited recruiter access and Fast Track Contact features.',
    benefits: [
      {
        icon: InfinityIcon,
        title: 'Unlimited Recruiter Searches',
        description: 'Open recruiter searches for every company without daily limits.',
      },
      {
        icon: LinkedInIcon,
        title: 'Message LinkedIn Recruiters',
        description: 'Open LinkedIn recruiter searches for any company and start outreach immediately.',
      },
      {
        icon: MailIcon,
        title: 'Auto-Generated Email Patterns',
        description: 'Get likely recruiter email addresses instantly.',
      },
      {
        icon: MapPinIcon,
        title: 'Office Location Mapping',
        description: 'Find company offices for walk-in applications.',
      },
      {
        icon: PhoneIcon,
        title: 'Phone / Email Scripts',
        description: 'Get past gatekeepers and talk to decision makers.',
      },
      {
        icon: TrendingUpIcon,
        title: 'Advanced Match Scoring',
        description: 'Better algorithms to find your perfect job fit.',
      },
      {
        icon: ZapIcon,
        title: 'AI Cover Letters',
        description: 'Generate tailored cover letters for every application.',
      },
      {
        icon: TargetIcon,
        title: 'Priority Support',
        description: 'Get help faster when you need it.',
      },
    ],
  },
  provider: {
    headline: 'Hire the Right People, Fast',
    subtitle: 'AI-powered job postings, LinkedIn candidate search, and a pipeline that fills itself',
    ctaText: 'Start finding candidates faster',
    proStatusText: 'Enjoying all Pro benefits including AI postings, LinkedIn search, and unlimited candidate browse.',
    benefits: [
      {
        icon: SparklesIcon,
        title: 'AI-Powered Job Postings',
        description: 'Describe what you need in plain English — AI writes the full posting.',
      },
      {
        icon: LinkedInIcon,
        title: 'LinkedIn Candidate Finder',
        description: 'Find qualified candidates on LinkedIn for any role you post.',
      },
      {
        icon: UsersIcon,
        title: 'Unlimited Candidate Browse',
        description: 'Browse all job seekers on the platform with no limits.',
      },
      {
        icon: MailIcon,
        title: 'Direct Candidate Outreach',
        description: 'Get email patterns and contact info for candidates you want to reach.',
      },
      {
        icon: TrendingUpIcon,
        title: 'Interest Tracking & Analytics',
        description: 'See who\'s interested, track views, and measure posting performance.',
      },
      {
        icon: BuildingIcon,
        title: 'AI Company Profile',
        description: 'Generate a professional company profile that attracts top talent.',
      },
      {
        icon: ZapIcon,
        title: 'Featured Listings',
        description: 'Your postings get priority placement in search results.',
      },
      {
        icon: TargetIcon,
        title: 'Priority Support',
        description: 'Get help faster when you need it.',
      },
    ],
  },
};

const ROLE_HOMES = {
  seeker: '/jobseeker/saved',
  provider: '/jobseeker',
};

const ROLE_HOME_LABELS = {
  seeker: 'Go to My Jobs',
  provider: 'Go to Dashboard',
};

function GoPro({ userRole = 'seeker' }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const { isPremium, loading, isSignedIn, startCheckout, purchaseVerified, clearPurchaseVerified } = useSubscription();
  const [managingBilling, setManagingBilling] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Fire ViewContent when the /gopro page loads (measures intent, not just clicks)
  useEffect(() => {
    fbTrack('ViewContent', { content_name: 'GoPro', content_category: 'subscription', value: 4.00, currency: 'USD' });
    trackFunnelEvent('gopro_view', { user_role: userRole, is_signed_in: isSignedIn }, { clerkUserId: user?.id, path: '/gopro' });
  }, [isSignedIn, user?.id, userRole]);

  // Fire Purchase only after the app verifies the Stripe session directly.
  useEffect(() => {
    if (purchaseVerified) {
      fbTrack('Purchase', { value: 4.00, currency: 'USD' });
      trackFunnelEvent('purchase_verified', { user_role: userRole }, { clerkUserId: user?.id, path: '/gopro' });
      clearPurchaseVerified();
    }
  }, [purchaseVerified, clearPurchaseVerified, user?.id, userRole]);

  const content = ROLE_CONTENT[userRole] || ROLE_CONTENT.seeker;

  const handleUpgrade = async () => {
    if (!isSignedIn) {
      trackFunnelEvent('checkout_sign_in_required', { user_role: userRole }, { path: '/gopro' });
      setSnackbar({ open: true, message: 'Please sign in first to upgrade to Pro.', severity: 'warning' });
      return;
    }
    
    if (!user?.id) {
      trackFunnelEvent('checkout_user_missing', { user_role: userRole }, { path: '/gopro' });
      setSnackbar({ open: true, message: 'User not loaded. Please refresh the page.', severity: 'warning' });
      return;
    }
    
    try {
      setUpgrading(true);
      fbTrack('InitiateCheckout', { value: 4.00, currency: 'USD' });
      trackFunnelEvent('upgrade_clicked', { user_role: userRole }, { clerkUserId: user.id, path: '/gopro' });
      await startCheckout();
    } catch (err) {
      trackFunnelEvent(
        'checkout_create_failed',
        { user_role: userRole, detail: err?.response?.data?.detail || err?.message || 'checkout_failed' },
        { clerkUserId: user?.id, path: '/gopro' }
      );
      console.error('Failed to start checkout:', err);
      setSnackbar({ open: true, message: 'Failed to start checkout. Please try again or contact support.', severity: 'error' });
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageBilling = async () => {
    if (!user?.id) return;
    
    try {
      setManagingBilling(true);
      trackFunnelEvent('billing_portal_requested', { user_role: userRole }, { clerkUserId: user.id, path: '/gopro' });
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      setSnackbar({ open: true, message: 'Failed to open billing management. Please try again.', severity: 'error' });
    } finally {
      setManagingBilling(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 6 }}>
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 4 }}>
        <Box sx={{ mb: 2, color: 'secondary.main' }}>
          <CrownIcon size={56} strokeWidth={1.5} />
        </Box>
        <Typography variant="h3" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
          Jobs Jobs Jobs Pro
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
          {content.headline}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          {content.subtitle}
        </Typography>
      </Box>

      {/* Current Status */}
      {isPremium && (
        <Alert 
          severity="success" 
          icon={<CheckIcon size={24} />}
          sx={{ mb: 4, borderRadius: 2 }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            You're a Pro Member!
          </Typography>
          <Typography variant="body2">
            {content.proStatusText}
          </Typography>
        </Alert>
      )}

      {/* Action Section */}
      <Box textAlign="center" sx={{ mb: 6 }}>
        <Card variant="light" sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
          <CardContent>
            <Typography variant="h5" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
              {isPremium ? 'Manage Your Subscription' : 'Get Started with Pro'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {isPremium 
                ? 'Update payment method, view invoices, or cancel your subscription.'
                : `${content.ctaText} — upgrade now for just $4/month.`
              }
            </Typography>

            {isPremium ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleManageBilling}
                  disabled={managingBilling}
                  startIcon={managingBilling ? <CircularProgress size={20} /> : <SettingsIcon size={20} />}
                  sx={designSystem.button.contained}
                >
                  {managingBilling ? 'Opening...' : 'Manage Subscription & Billing'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => navigate(ROLE_HOMES[userRole] || '/jobseeker')}
                  sx={designSystem.button.outlined}
                >
                  {ROLE_HOME_LABELS[userRole] || 'Go to My Jobs'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isSignedIn ? (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleUpgrade}
                      disabled={upgrading}
                      startIcon={upgrading ? <CircularProgress size={20} /> : <StarIcon size={20} />}
                      sx={designSystem.button.contained}
                    >
                      {upgrading ? 'Starting Checkout...' : 'Upgrade to Pro - $4/month'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Secure payment via Stripe • Cancel anytime • Works across all roles
                    </Typography>
                  </>
                ) : (
                  <SignInButton mode="modal">
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                    >
                      Sign In to Upgrade
                    </Button>
                  </SignInButton>
                )}
              </Box>
            )}

            {!isPremium && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Already a Pro member?{' '}
                <Button 
                  variant="text" 
                  size="small"
                  onClick={handleManageBilling}
                  disabled={!isSignedIn || managingBilling}
                >
                  Manage Billing
                </Button>
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Pro Benefits */}
      <Divider sx={{ mb: 6 }} />
      
      <Box>
        <Typography variant="h5" fontWeight="600" textAlign="center" sx={{ mb: 4, color: 'text.primary' }}>
          What You Get with Pro
        </Typography>
        <Grid container spacing={3}>
          {content.benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="interactive" sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ color: 'secondary.main', mb: 2 }}>
                    <IconComponent size={32} strokeWidth={1.5} />
                  </Box>
                  <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Snackbar for user feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default GoPro;
